import type { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import { Badge } from '../shadcn/ui/badge';
import LowNumFormatter from './LowNumFormatter';
import dayjs from 'dayjs';
import { Avatar } from '../core/Avatar';
import { Button } from '../shadcn/ui/button';
import UsernameByPublicKey from './UsernameByPublicKey';
import { getWrappedAsset, getWrappedAssetIcon } from '../../utils/deso';
import { DESO_ZERO_PUBLIC_KEY } from '../../constants/AppConstants';
import { TwapOrderResponse } from 'deso-protocol/src/transactions/amm';
import { cn } from '../../utils/shadcn';
import { pluralize } from '../../utils/text';

export enum TwapStatus {
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
  PARTIALLY_FILLED = 'PARTIALLY_FILLED',
  FILLED = 'FILLED',
}

const progressColors: Record<TwapStatus, string> = {
  [TwapStatus.READY]: 'bg-yellow-500',
  [TwapStatus.IN_PROGRESS]: 'bg-blue-500',
  [TwapStatus.CANCELLED]: 'bg-gray-500',
  [TwapStatus.FAILED]: 'bg-red-500',
  [TwapStatus.PARTIALLY_FILLED]: 'bg-orange-500',
  [TwapStatus.FILLED]: 'bg-green-500',
};

const statusColors: Record<TwapStatus, string> = {
  [TwapStatus.READY]: 'text-yellow-500 border-yellow-500',
  [TwapStatus.IN_PROGRESS]: 'text-yellow-500 border-yellow-500',
  [TwapStatus.CANCELLED]: 'text-gray-500 border-gray-500',
  [TwapStatus.FAILED]: 'text-red-500 border-red-500',
  [TwapStatus.PARTIALLY_FILLED]: 'text-yellow-500 border-yellow-500',
  [TwapStatus.FILLED]: 'text-green-500 border-green-500',
};

export const twapColumns: ColumnDef<TwapOrderResponse>[] = [
  {
    accessorKey: 'Side',
    header: 'Side',
    cell: ({ row }) => (
      <Badge className={row.original.Side === 'BID' ? 'bg-green-500' : 'bg-red-500'}>
        {row.original.Side === 'BID' ? 'BUY' : 'SELL'}
      </Badge>
    ),
  },
  {
    accessorKey: 'Status',
    header: 'Progress',
    cell: ({ row }) => {
      const progress =
        row.original.Status === 'FILLED'
          ? 100
          : (row.original.TwapStats.TradedQuantity / row.original.TwapStats.TargetQuantity) * 100;
      const progressBarColor = progressColors[row.original.Status as TwapStatus] || 'bg-muted';
      const visibleProgress = Math.min(100, Math.max(0, progress));

      return (
        <div className="flex flex-col w-full pr-2 py-2">
          <div
            className={cn(
              'w-full rounded-full h-[10px] mb-1',
              row.original.Status === 'FAILED' ? 'bg-red-500' : 'bg-muted',
            )}
          >
            <div className={cn(`h-[10px] rounded-full`, progressBarColor)} style={{ width: `${progress}%` }}></div>
          </div>
          <span className="text-xxs text-muted whitespace-nowrap truncate">
            {visibleProgress.toFixed(0)}% at avg.{' '}
            <LowNumFormatter className="text-xxs" price={row.original.TwapStats.AvgPriceUsd} />
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: 'QuoteCurrencyPubKey',
    header: 'Quote',
    cell: ({ row }) => {
      const effectivePublicKet =
        row.original.QuoteCurrencyPubKey === DESO_ZERO_PUBLIC_KEY ? 'DESO' : row.original.QuoteCurrencyPubKey;

      const wrappedAsset = getWrappedAsset(effectivePublicKet, 'publicKey');

      return (
        <div className="flex gap-x-1 items-center w-full">
          <Avatar
            size="xs"
            className="w-6"
            src={!!wrappedAsset ? getWrappedAssetIcon(wrappedAsset) : row.original.QuoteCurrencyPubKey}
          />
          <UsernameByPublicKey
            publicKey={row.original.QuoteCurrencyPubKey}
            className="text-foreground text-sm truncate"
          />
        </div>
      );
    },
  },
  {
    accessorKey: 'BaseCurrencyPubKey',
    header: 'Base',
    cell: ({ row }) => {
      const effectivePublicKet =
        row.original.BaseCurrencyPubKey === DESO_ZERO_PUBLIC_KEY ? 'DESO' : row.original.BaseCurrencyPubKey;

      const wrappedAsset = getWrappedAsset(effectivePublicKet, 'publicKey');

      return (
        <div className="flex gap-x-1 items-center w-full">
          <Avatar
            size="xs"
            className="w-6"
            src={!!wrappedAsset ? getWrappedAssetIcon(wrappedAsset) : row.original.BaseCurrencyPubKey}
          />
          <UsernameByPublicKey
            publicKey={row.original.BaseCurrencyPubKey}
            className="text-foreground text-sm truncate whitespace-nowrap"
          />
        </div>
      );
    },
  },
  {
    accessorKey: 'AmountInBaseCurrency',
    header: 'Amount',
    cell: ({ row }) => (
      <div className="flex gap-x-1 items-center w-full">
        <LowNumFormatter
          price={Number(row.original.AmountInBaseCurrency)}
          isUsd={false}
          className="text-foreground font-medium"
        />
        <UsernameByPublicKey
          publicKey={row.original.BaseCurrencyPubKey}
          customPrefix="$"
          className="text-foreground text-sm truncate"
        />
      </div>
    ),
  },
  {
    accessorKey: 'LimitPriceInUsd',
    header: 'Limit Price',
    cell: ({ row }) => {
      const highlightColor = row.original.Side === 'BID' ? 'text-green-500' : 'text-red-500';

      return (
        <div className="flex gap-x-1 items-center">
          <LowNumFormatter
            className={cn('font-medium', highlightColor)}
            price={Number(row.original.LimitPriceInUsd || row.original.LimitPriceInQuoteCurrency)}
            isUsd={!!row.original.LimitPriceInUsd}
          />
          {row.original.LimitPriceInQuoteCurrency ? (
            <UsernameByPublicKey
              publicKey={row.original.BaseCurrencyPubKey}
              customPrefix="$"
              className={cn(`text-sm`, highlightColor)}
            />
          ) : (
            <span className={cn(`text-sm`, highlightColor)}>USD</span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'StartAt',
    header: 'Start Time',
    cell: ({ row }) => {
      const seconds = row.original.DurationSeconds;
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      return (
        <div className="w-full">
          <div className="text-xs text-foreground whitespace-nowrap truncate">
            {dayjs(row.original.StartAt).format('MMM D, h:mm A')}
          </div>
          <div className="text-xs whitespace-nowrap truncate">
            Duration: {hours > 0 ? pluralize(hours, 'hour') : pluralize(minutes, 'minute')}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      return (
        <Badge variant="outline" className={statusColors[row.original.Status as TwapStatus]}>
          {row.original.Status}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'Actions',
    header: 'Actions',
    cell: (e) => {
      const canCancel = e.row.original.Status === 'READY' || e.row.original.Status === 'IN_PROGRESS';

      if (!canCancel) {
        return;
      }

      return (
        <div className="w-full text-end">
          <Button
            disabled={(e.table.options.meta as any)?.cancellingOrderId === e.row.original.TwapConfigId}
            variant="destructive"
            size="sm"
            onClick={() => (e.table.options.meta as any)?.onCancel?.(e.row.original.TwapConfigId)}
          >
            Cancel
          </Button>
        </div>
      );
    },
  },
];
